import { useState, useEffect, useRef } from 'react';
import { useI18n } from '../context/I18nContext';
import { getSoundSettings, saveSoundSettings, previewThinkingSound, previewAnswerDing } from '../utils/sound';
import { API_BASE } from '../config';

const MISTRAL_LANGS = [
  { code: 'fr', label: 'Français' },
  { code: 'en', label: 'English' },
  { code: 'es', label: 'Español' },
  { code: 'de', label: 'Deutsch' },
  { code: 'it', label: 'Italiano' },
  { code: 'pt', label: 'Português' },
  { code: 'ar', label: 'العربية' }
];



export default function SoundSettings({ isAdmin, isSuperAdmin, elevenLabsEnabled, elevenLabsLoading, toggleElevenLabs }) {
  const { t } = useI18n();
  const [settings, setSettings] = useState(getSoundSettings());
  const [message, setMessage] = useState('');

  // ── Mistral voice (TTS/STT) ───────────────────────────────────────────
  const [mistral, setMistral] = useState(null);          // settings from backend
  const [mistralAvail, setMistralAvail] = useState(null); // { available, reason }
  const [mistralSaving, setMistralSaving] = useState(false);
  const [mistralMsg, setMistralMsg] = useState('');
  const [ttsTestText, setTtsTestText] = useState('Bonjour, ceci est un test de la voix Mistral.');
  const [ttsTesting, setTtsTesting] = useState(false);
  const [sttText, setSttText] = useState('');
  const [recording, setRecording] = useState(false);
  const [sttBusy, setSttBusy] = useState(false);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const audioRef = useRef(null);
  const token = typeof localStorage !== 'undefined' ? localStorage.getItem('cogency_token') : null;

  // ── Unified voice provider selection (Option A: one active at a time) ───
  const [voiceProviders, setVoiceProviders] = useState([]);
  const [activeProviderId, setActiveProviderId] = useState(null);
  const [providersLoading, setProvidersLoading] = useState(false);
  const [providersMsg, setProvidersMsg] = useState('');

  // Load providers list and active provider
  useEffect(() => {
    let cancelled = false;
    const loadProviders = async () => {
      try {
        setProvidersLoading(true);
        const [listRes, activeRes] = await Promise.all([
          fetch(`${API_BASE}/api/voice/providers`),
          fetch(`${API_BASE}/api/voice/providers/active`)
        ]);
        if (cancelled) return;
        if (listRes.ok) {
          const listData = await listRes.json();
          setVoiceProviders(listData.providers || []);
        }
        if (activeRes.ok) {
          const activeData = await activeRes.json();
          setActiveProviderId(activeData.activeProviderId || null);
        }
      } catch (e) {
        console.error('[SoundSettings] Failed to load voice providers:', e);
      } finally {
        if (!cancelled) setProvidersLoading(false);
      }
    };
    loadProviders();
    return () => { cancelled = true; };
  }, []);

  const activateProvider = async (id) => {
    setProvidersMsg('');
    try {
      const res = await fetch(`${API_BASE}/api/voice/providers/active`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ providerId: id })
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) {
        setActiveProviderId(id);
        setProvidersMsg(t('providerActivated') || 'Provider activé');
        // refresh availability
        const listRes = await fetch(`${API_BASE}/api/voice/providers`);
        if (listRes.ok) {
          const listData = await listRes.json();
          setVoiceProviders(listData.providers || []);
        }
      } else {
        setProvidersMsg((t('providerActivationFailed') || 'Activation échouée') + (data.error ? `: ${data.error}` : ''));
      }
    } catch (e) {
      setProvidersMsg((t('providerActivationFailed') || 'Activation échouée') + `: ${e.message}`);
    }
    setTimeout(() => setProvidersMsg(''), 3000);
  };

  const formatRoles = (roles) => {
    const labels = [];
    if (roles?.includes('tts')) labels.push(t('roleTTS') || 'Synthèse');
    if (roles?.includes('stt')) labels.push(t('roleSTT') || 'Reconnaissance');
    return labels.join(' + ') || t('roleNone') || '-';
  };

  // ── Groq voice (Whisper STT) ──────────────────────────────────────────
  const [groq, setGroq] = useState(null);
  const [groqAvail, setGroqAvail] = useState(null);
  const [groqSaving, setGroqSaving] = useState(false);
  const [groqMsg, setGroqMsg] = useState('');
  const [groqSttText, setGroqSttText] = useState('');
  const [groqRecording, setGroqRecording] = useState(false);
  const [groqSttBusy, setGroqSttBusy] = useState(false);
  const groqRecorderRef = useRef(null);
  const groqChunksRef = useRef([]);

  useEffect(() => {
    saveSoundSettings(settings);
  }, [settings]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [sRes, aRes] = await Promise.all([
          fetch(`${API_BASE}/api/voice/mistral/settings`),
          fetch(`${API_BASE}/api/voice/mistral/availability`)
        ]);
        if (cancelled) return;
        if (sRes.ok) setMistral(await sRes.json());
        if (aRes.ok) setMistralAvail(await aRes.json());
      } catch {
        if (!cancelled) setMistralAvail({ available: false, reason: 'unreachable' });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const updateMistral = async (patch) => {
    const next = { ...(mistral || {}), ...patch };
    setMistral(next);
    setMistralSaving(true);
    setMistralMsg('');
    try {
      const res = await fetch(`${API_BASE}/api/voice/mistral/settings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify(patch)
      });
      if (res.ok) {
        setMistral(await res.json());
        setMistralMsg(t('mistralSaved') || 'Enregistré');
        setTimeout(() => setMistralMsg(''), 1800);
      } else {
        setMistralMsg(t('mistralSaveError') || 'Échec de l\'enregistrement');
      }
    } catch {
      setMistralMsg(t('mistralSaveError') || 'Échec de l\'enregistrement');
    }
    setMistralSaving(false);
  };

  const testMistralTTS = async () => {
    if (!ttsTestText.trim()) return;
    setTtsTesting(true);
    setMistralMsg('');
    try {
      const lang = mistral?.defaultLanguage || 'fr';
      const url = `${API_BASE}/api/tts-mistral?text=${encodeURIComponent(ttsTestText.slice(0, 500))}&lang=${lang}`;
      const res = await fetch(url);
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setMistralMsg((t('mistralTtsFailed') || 'Échec TTS Mistral') + (err.error ? ` : ${err.error}` : ''));
        setTtsTesting(false);
        return;
      }
      const blob = await res.blob();
      const audioUrl = URL.createObjectURL(blob);
      if (audioRef.current) {
        audioRef.current.src = audioUrl;
        await audioRef.current.play().catch(() => {});
      }
    } catch (e) {
      setMistralMsg((t('mistralTtsFailed') || 'Échec TTS Mistral') + ` : ${e.message}`);
    }
    setTtsTesting(false);
  };

  const startRecording = async () => {
    setMistralMsg('');
    setSttText('');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach(tr => tr.stop());
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        await transcribeBlob(blob);
      };
      mediaRecorderRef.current = mr;
      mr.start();
      setRecording(true);
    } catch (e) {
      setMistralMsg((t('micError') || 'Accès micro refusé') + ` : ${e.message}`);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && recording) {
      mediaRecorderRef.current.stop();
      setRecording(false);
    }
  };

  const transcribeBlob = async (blob) => {
    setSttBusy(true);
    try {
      const b64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
      const lang = mistral?.defaultLanguage || 'fr';
      const res = await fetch(`${API_BASE}/api/stt-mistral`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ audio: b64, lang, mime: 'audio/webm' })
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) {
        setSttText(data.text || '');
      } else {
        setMistralMsg((t('mistralSttFailed') || 'Échec STT Mistral') + (data.error ? ` : ${data.error}` : ''));
      }
    } catch (e) {
      setMistralMsg((t('mistralSttFailed') || 'Échec STT Mistral') + ` : ${e.message}`);
    }
    setSttBusy(false);
  };

  // ── Groq (Whisper STT) ────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [sRes, aRes] = await Promise.all([
          fetch(`${API_BASE}/api/voice/groq/settings`),
          fetch(`${API_BASE}/api/voice/groq/availability`)
        ]);
        if (cancelled) return;
        if (sRes.ok) setGroq(await sRes.json());
        if (aRes.ok) setGroqAvail(await aRes.json());
      } catch {
        if (!cancelled) setGroqAvail({ available: false, reason: 'unreachable' });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const updateGroq = async (patch) => {
    const next = { ...(groq || {}), ...patch };
    setGroq(next);
    setGroqSaving(true);
    setGroqMsg('');
    try {
      const res = await fetch(`${API_BASE}/api/voice/groq/settings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify(patch)
      });
      if (res.ok) {
        setGroq(await res.json());
        setGroqMsg(t('mistralSaved') || 'Enregistré');
        setTimeout(() => setGroqMsg(''), 1800);
      } else {
        setGroqMsg(t('mistralSaveError') || 'Échec de l\'enregistrement');
      }
    } catch {
      setGroqMsg(t('mistralSaveError') || 'Échec de l\'enregistrement');
    }
    setGroqSaving(false);
  };

  const startGroqRecording = async () => {
    setGroqMsg('');
    setGroqSttText('');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      groqChunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) groqChunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach(tr => tr.stop());
        const blob = new Blob(groqChunksRef.current, { type: 'audio/webm' });
        await transcribeGroqBlob(blob);
      };
      groqRecorderRef.current = mr;
      mr.start();
      setGroqRecording(true);
    } catch (e) {
      setGroqMsg((t('micError') || 'Accès micro refusé') + ` : ${e.message}`);
    }
  };

  const stopGroqRecording = () => {
    if (groqRecorderRef.current && groqRecording) {
      groqRecorderRef.current.stop();
      setGroqRecording(false);
    }
  };

  const transcribeGroqBlob = async (blob) => {
    setGroqSttBusy(true);
    try {
      const b64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
      const lang = groq?.defaultLanguage || 'fr';
      const res = await fetch(`${API_BASE}/api/stt-groq`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ audio: b64, lang, mime: 'audio/webm' })
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) {
        setGroqSttText(data.text || '');
      } else {
        setGroqMsg((t('groqSttFailed') || 'Échec STT Groq') + (data.error ? ` : ${data.error}` : ''));
      }
    } catch (e) {
      setGroqMsg((t('groqSttFailed') || 'Échec STT Groq') + ` : ${e.message}`);
    }
    setGroqSttBusy(false);
  };

  const update = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const handlePreviewThinking = () => {
    previewThinkingSound();
  };

  const handlePreviewDing = () => {
    previewAnswerDing();
  };

  const resetDefaults = () => {
    const defaults = { thinkingEnabled: true, dingEnabled: true, volume: 0.7, thinkingInterval: 1.5, waitToneSpeed: 1.0 };
    setSettings(defaults);
    setMessage(t('settingsReset') || 'Settings reset to defaults');
    setTimeout(() => setMessage(''), 2000);
  };

  return (
    <div className="sound-settings-panel">
      <h3>🔔 {t('soundSettings') || 'Sound Settings'}</h3>
      <p className="section-desc">{t('soundSettingsDesc') || 'Customize thinking and answer sounds.'}</p>

      <div className="sound-setting-option">
        <div className="sound-setting-info">
          <span className="sound-setting-label">🧠 {t('thinkingSound') || 'Thinking Sound'}</span>
          <span className="sound-setting-desc">{t('thinkingSoundDesc') || 'Play while agents are thinking'}</span>
        </div>
        <button
          className={`accessibility-toggle ${settings.thinkingEnabled ? 'active' : ''}`}
          onClick={() => update('thinkingEnabled', !settings.thinkingEnabled)}
          aria-pressed={settings.thinkingEnabled}
        >
          {settings.thinkingEnabled ? '✓ On' : 'Off'}
        </button>
      </div>

      <div className="sound-setting-option">
        <div className="sound-setting-info">
          <span className="sound-setting-label">✨ {t('answerDing') || 'Answer Ding'}</span>
          <span className="sound-setting-desc">{t('answerDingDesc') || 'Play when an answer is received'}</span>
        </div>
        <button
          className={`accessibility-toggle ${settings.dingEnabled ? 'active' : ''}`}
          onClick={() => update('dingEnabled', !settings.dingEnabled)}
          aria-pressed={settings.dingEnabled}
        >
          {settings.dingEnabled ? '✓ On' : 'Off'}
        </button>
      </div>

      <div className="sound-setting-option vertical">
        <div className="sound-setting-info">
          <span className="sound-setting-label">🔊 {t('volume') || 'Volume'}</span>
          <span className="sound-setting-desc">{Math.round(settings.volume * 100)}%</span>
        </div>
        <input
          type="range"
          min="0"
          max="1"
          step="0.05"
          value={settings.volume}
          onChange={e => update('volume', parseFloat(e.target.value))}
          className="sound-volume-slider"
          aria-label={t('volume') || 'Volume'}
        />
      </div>

      <div className="sound-setting-option vertical">
        <div className="sound-setting-info">
          <span className="sound-setting-label">⏱️ {t('thinkingTempo') || 'Thinking Tempo'}</span>
          <span className="sound-setting-desc">{t('thinkingTempoDesc') || 'Speed of the thinking clock ticks'}</span>
        </div>
        <div className="sound-tempo-row">
          <span className="sound-tempo-label">{t('slow') || 'Slow'}</span>
          <input
            type="range"
            min="0.3"
            max="2.5"
            step="0.1"
            value={settings.thinkingInterval}
            onChange={e => update('thinkingInterval', parseFloat(e.target.value))}
            className="sound-tempo-slider"
            aria-label={t('thinkingTempo') || 'Thinking Tempo'}
          />
          <span className="sound-tempo-label">{t('fast') || 'Fast'}</span>
          <span className="sound-tempo-value">{settings.thinkingInterval.toFixed(1)}s</span>
        </div>
      </div>

      <div className="sound-setting-option vertical">
        <div className="sound-setting-info">
          <span className="sound-setting-label">📞 {t('waitTone') || 'Wait Tone'}</span>
          <span className="sound-setting-desc">{t('waitToneDesc') || 'Speed of the wait/hold tone'}</span>
        </div>
        <div className="sound-tempo-row">
          <span className="sound-tempo-label">{t('slow') || 'Slow'}</span>
          <input
            type="range"
            min="0.5"
            max="2.0"
            step="0.1"
            value={settings.waitToneSpeed || 1.0}
            onChange={e => update('waitToneSpeed', parseFloat(e.target.value))}
            className="sound-tempo-slider"
            aria-label={t('waitTone') || 'Wait Tone'}
          />
          <span className="sound-tempo-label">{t('fast') || 'Fast'}</span>
          <span className="sound-tempo-value">{((settings.waitToneSpeed || 1.0)).toFixed(1)}x</span>
        </div>
      </div>

      <div className="sound-preview-row">
        <button className="btn-secondary btn-small" onClick={handlePreviewThinking} disabled={!settings.thinkingEnabled}>
          ▶ {t('previewThinking') || 'Preview Thinking'}
        </button>
        <button className="btn-secondary btn-small" onClick={handlePreviewDing} disabled={!settings.dingEnabled}>
          ▶ {t('previewDing') || 'Preview Ding'}
        </button>
      </div>

      {(isAdmin || isSuperAdmin) && (
        <div className="field-group admin-toggle-row" style={{ marginTop: '20px', borderTop: '1px solid var(--border)', paddingTop: '20px' }}>
          <div className="toggle-text">
            <span className="toggle-title">🔊 {t('enableElevenLabs') || 'Enable ElevenLabs'}</span>
            <span className="toggle-caption">{t('elevenLabsCaption') || 'Use ElevenLabs API for high-quality voice synthesis.'}</span>
          </div>
          <label className="toggle-label">
            <input
              type="checkbox"
              checked={elevenLabsEnabled}
              onChange={toggleElevenLabs}
              disabled={elevenLabsLoading}
              aria-label={t('enableElevenLabs') || 'Enable ElevenLabs'}
            />
            <span className="toggle-slider"></span>
          </label>
        </div>
      )}

      {/* ── Unified active voice provider selection (Option A) ── */}
      {(isAdmin || isSuperAdmin) && (
        <div className="voice-providers-section" style={{ marginTop: '24px', borderTop: '1px solid var(--border)', paddingTop: '20px' }}>
          <h4 style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            🎙️ {t('voiceProviders') || 'Moteur vocal actif'}
            {providersLoading && <span style={{ fontSize: '12px', color: 'var(--text-muted)', fontWeight: 400 }}>{t('loading') || 'Chargement...'}</span>}
          </h4>
          <p className="section-desc">
            {t('voiceProvidersDesc') || 'Un seul moteur vocal peut être actif à la fois. Sélectionnez celui qui sera utilisé pour la synthèse et/ou la reconnaissance vocale.'}
          </p>

          <div className="voice-providers-grid" style={{ display: 'grid', gap: '10px', marginTop: '12px' }}>
            {voiceProviders.map(provider => {
              const isActive = activeProviderId === provider.id;
              return (
                <div
                  key={provider.id}
                  className={`voice-provider-card ${isActive ? 'active' : ''} ${provider.available ? 'available' : 'unavailable'}`}
                  onClick={() => { if (provider.available) activateProvider(provider.id); }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '12px',
                    borderRadius: '10px',
                    border: `2px solid ${isActive ? 'var(--primary)' : 'var(--border)'}`,
                    background: isActive ? 'var(--primary-soft, rgba(99,102,241,0.08))' : 'var(--bg-card, var(--bg-secondary))',
                    opacity: provider.available ? 1 : 0.55,
                    cursor: provider.available ? 'pointer' : 'not-allowed',
                    transition: 'all 0.15s ease'
                  }}
                >
                  <input
                    type="radio"
                    name="activeVoiceProvider"
                    checked={isActive}
                    onChange={() => { if (provider.available) activateProvider(provider.id); }}
                    disabled={!provider.available || providersLoading}
                    aria-label={`${provider.name} ${formatRoles(provider.roles)}`}
                    style={{ width: '18px', height: '18px', accentColor: 'var(--primary)', flexShrink: 0 }}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                      <span className="voice-provider-name" style={{ fontWeight: 600, fontSize: '14px' }}>{provider.name}</span>
                      <span
                        style={{
                          fontSize: '10px',
                          fontWeight: 600,
                          padding: '2px 8px',
                          borderRadius: '999px',
                          background: provider.available ? 'rgba(16,185,129,0.12)' : 'rgba(239,68,68,0.12)',
                          color: provider.available ? '#10b981' : '#ef4444'
                        }}
                      >
                        {provider.available ? (t('available') || 'Disponible') : (t('unavailable') || 'Indisponible')}
                      </span>
                      <span style={{ fontSize: '10px', color: 'var(--text-muted)', background: 'var(--bg-hover)', padding: '2px 8px', borderRadius: '999px' }}>
                        {formatRoles(provider.roles)}
                      </span>
                    </div>
                    <p style={{ margin: '4px 0 0', fontSize: '12px', color: 'var(--text-muted)', lineHeight: 1.4 }}>
                      {provider.description}
                    </p>
                    {!provider.available && provider.reason && (
                      <p style={{ margin: '4px 0 0', fontSize: '11px', color: '#ef4444' }}>
                        {provider.reason}
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {providersMsg && (
            <p style={{ fontSize: '12px', marginTop: '12px', color: providersMsg.includes('échouée') || providersMsg.includes('failed') ? '#ef4444' : '#10b981' }}>
              {providersMsg}
            </p>
          )}
        </div>
      )}

      {(isAdmin || isSuperAdmin) && (
        <div className="mistral-voice-section" style={{ marginTop: '24px', borderTop: '1px solid var(--border)', paddingTop: '20px' }}>
          <h4 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            🎙️ {t('mistralVoice') || 'Voix Mistral (Voxtral)'}
            {mistralAvail && (
              <span
                className={`mistral-status-badge ${mistralAvail.available ? 'ok' : 'off'}`}
                style={{
                  fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: '999px',
                  background: mistralAvail.available ? 'rgba(16,185,129,0.15)' : 'rgba(239,68,68,0.15)',
                  color: mistralAvail.available ? '#10b981' : '#ef4444'
                }}
              >
                {mistralAvail.available ? (t('connected') || 'Connecté') : (t('unavailable') || 'Indisponible')}
              </span>
            )}
          </h4>
          <p className="section-desc">
            {t('mistralVoiceDesc') || 'Synthèse vocale (texte → voix) et reconnaissance vocale (voix → texte) via les modèles Voxtral de Mistral.'}
          </p>

          {mistralAvail && !mistralAvail.available && (
            <div style={{ fontSize: '12px', color: '#ef4444', marginBottom: '12px' }}>
              ⚠️ {mistralAvail.reason || (t('mistralUnavailableHint') || 'Vérifiez la clé MISTRAL_API_KEY dans la configuration serveur.')}
            </div>
          )}

          {mistral && (
            <>
              <div className="sound-setting-option vertical">
                <div className="sound-setting-info">
                  <span className="sound-setting-label">🔊 {t('mistralTts') || 'Synthèse vocale (TTS)'}</span>
                  <span className="sound-setting-desc">{t('mistralTtsDesc') || 'Générer de la voix à partir du texte'}</span>
                </div>
                <button
                  className={`accessibility-toggle ${mistral.ttsEnabled ? 'active' : ''}`}
                  onClick={() => updateMistral({ ttsEnabled: !mistral.ttsEnabled })}
                  disabled={mistralSaving}
                  aria-pressed={!!mistral.ttsEnabled}
                >
                  {mistral.ttsEnabled ? '✓ On' : 'Off'}
                </button>
              </div>

              <div className="sound-setting-option vertical">
                <div className="sound-setting-info">
                  <span className="sound-setting-label">🎤 {t('mistralStt') || 'Reconnaissance vocale (STT)'}</span>
                  <span className="sound-setting-desc">{t('mistralSttDesc') || 'Transcrire le texte à partir de la voix'}</span>
                </div>
                <button
                  className={`accessibility-toggle ${mistral.sttEnabled ? 'active' : ''}`}
                  onClick={() => updateMistral({ sttEnabled: !mistral.sttEnabled })}
                  disabled={mistralSaving}
                  aria-pressed={!!mistral.sttEnabled}
                >
                  {mistral.sttEnabled ? '✓ On' : 'Off'}
                </button>
              </div>

              <div className="sound-setting-option vertical">
                <div className="sound-setting-info">
                  <span className="sound-setting-label">🌐 {t('language') || 'Langue'}</span>
                  <span className="sound-setting-desc">{t('mistralLangDesc') || 'Langue par défaut pour TTS/STT'}</span>
                </div>
                <select
                  value={mistral.defaultLanguage || 'fr'}
                  onChange={e => updateMistral({ defaultLanguage: e.target.value })}
                  className="setup-input"
                  disabled={mistralSaving}
                  style={{ maxWidth: '220px' }}
                >
                  {MISTRAL_LANGS.map(l => (
                    <option key={l.code} value={l.code}>{l.label}</option>
                  ))}
                </select>
              </div>

              {isSuperAdmin && (
                <div className="sound-setting-option vertical">
                  <div className="sound-setting-info">
                    <span className="sound-setting-label">🆔 {t('mistralVoiceId') || 'Voice ID (TTS)'}</span>
                    <span className="sound-setting-desc">{t('mistralVoiceIdDesc') || 'Identifiant de voix Voxtral créé dans Mistral Studio (requis pour la synthèse).'}</span>
                  </div>
                  <input
                    type="text"
                    value={mistral.defaultVoiceId || ''}
                    onChange={e => setMistral(prev => ({ ...prev, defaultVoiceId: e.target.value }))}
                    onBlur={e => updateMistral({ defaultVoiceId: e.target.value.trim() })}
                    placeholder="voxtral-voice-id"
                    className="setup-input"
                    style={{ maxWidth: '320px' }}
                  />
                </div>
              )}

              <div className="sound-setting-option vertical" style={{ marginTop: '8px' }}>
                <div className="sound-setting-info">
                  <span className="sound-setting-label">▶ {t('mistralTtsTest') || 'Tester la synthèse vocale'}</span>
                </div>
                <textarea
                  value={ttsTestText}
                  onChange={e => setTtsTestText(e.target.value)}
                  rows={2}
                  className="setup-input"
                  placeholder={t('mistralTtsPlaceholder') || 'Saisissez un texte à lire à voix haute...'}
                  style={{ width: '100%', resize: 'vertical' }}
                />
                <button
                  className="btn-primary btn-small"
                  onClick={testMistralTTS}
                  disabled={ttsTesting || !mistral.ttsEnabled || !ttsTestText.trim() || (mistralAvail && !mistralAvail.available)}
                  style={{ marginTop: '8px', alignSelf: 'flex-start' }}
                >
                  {ttsTesting ? (t('loading') || 'Chargement...') : ('🔊 ' + (t('mistralPlay') || 'Lire'))}
                </button>
                <audio ref={audioRef} style={{ display: 'none' }} />
              </div>

              <div className="sound-setting-option vertical" style={{ marginTop: '8px' }}>
                <div className="sound-setting-info">
                  <span className="sound-setting-label">🎙️ {t('mistralSttTest') || 'Tester la reconnaissance vocale'}</span>
                  <span className="sound-setting-desc">{t('mistralSttTestDesc') || 'Enregistrez votre voix pour la transcrire en texte'}</span>
                </div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                  {!recording ? (
                    <button
                      className="btn-primary btn-small"
                      onClick={startRecording}
                      disabled={sttBusy || !mistral.sttEnabled || (mistralAvail && !mistralAvail.available)}
                    >
                      ⏺ {t('mistralRecord') || 'Enregistrer'}
                    </button>
                  ) : (
                    <button className="btn-danger btn-small" onClick={stopRecording}>
                      ⏹ {t('mistralStop') || 'Arrêter'}
                    </button>
                  )}
                  {sttBusy && <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{t('transcribing') || 'Transcription...'}</span>}
                  {recording && <span style={{ fontSize: '12px', color: '#ef4444' }}>● {t('recording') || 'Enregistrement...'}</span>}
                </div>
                {sttText && (
                  <div style={{ marginTop: '8px', padding: '8px 10px', background: 'var(--bg-hover)', borderRadius: '8px', fontSize: '13px' }}>
                    {sttText}
                  </div>
                )}
              </div>

              {mistralMsg && (
                <p style={{ fontSize: '12px', color: mistralMsg.includes('Échec') || mistralMsg.includes('refusé') ? '#ef4444' : '#10b981', marginTop: '8px' }}>
                  {mistralMsg}
                </p>
              )}
            </>
          )}
        </div>
      )}

      {(isAdmin || isSuperAdmin) && (
        <div className="groq-voice-section" style={{ marginTop: '24px', borderTop: '1px solid var(--border)', paddingTop: '20px' }}>
          <h4 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            🗣️ {t('groqVoice') || 'Reconnaissance vocale Groq (Whisper)'}
          {groqAvail && (
            <span
              className={`groq-status-badge ${groqAvail.available ? 'ok' : 'off'}`}
              style={{
                fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: '999px',
                background: groqAvail.available ? 'rgba(16,185,129,0.15)' : 'rgba(239,68,68,0.15)',
                color: groqAvail.available ? '#10b981' : '#ef4444'
              }}
            >
              {groqAvail.available ? (t('connected') || 'Connecté') : (t('unavailable') || 'Indisponible')}
            </span>
          )}
        </h4>
        <p className="section-desc">
          {t('groqVoiceDesc') || 'Reconnaissance vocale (voix → texte) rapide via le modèle Whisper de Groq. Complément idéal à Grok pour le raisonnement.'}
        </p>

        {groqAvail && !groqAvail.available && (
          <div style={{ fontSize: '12px', color: '#ef4444', marginBottom: '12px' }}>
            ⚠️ {groqAvail.reason || (t('groqUnavailableHint') || 'Vérifiez la clé GROQ_API_KEY dans la configuration serveur.')}
          </div>
        )}

        {groq && (
          <>
            <div className="sound-setting-option vertical">
              <div className="sound-setting-info">
                <span className="sound-setting-label">🎤 {t('groqStt') || 'Reconnaissance vocale (STT)'}</span>
                <span className="sound-setting-desc">{t('mistralSttDesc') || 'Transcrire le texte à partir de la voix'}</span>
              </div>
              <button
                className={`accessibility-toggle ${groq.sttEnabled ? 'active' : ''}`}
                onClick={() => updateGroq({ sttEnabled: !groq.sttEnabled })}
                disabled={groqSaving}
                aria-pressed={!!groq.sttEnabled}
              >
                {groq.sttEnabled ? '✓ On' : 'Off'}
              </button>
            </div>

            <div className="sound-setting-option vertical">
              <div className="sound-setting-info">
                <span className="sound-setting-label">🌐 {t('language') || 'Langue'}</span>
                <span className="sound-setting-desc">{t('groqLangDesc') || 'Langue par défaut pour la transcription'}</span>
              </div>
              <select
                value={groq.defaultLanguage || 'fr'}
                onChange={e => updateGroq({ defaultLanguage: e.target.value })}
                className="setup-input"
                disabled={groqSaving}
                style={{ maxWidth: '220px' }}
              >
                {MISTRAL_LANGS.map(l => (
                  <option key={l.code} value={l.code}>{l.label}</option>
                ))}
              </select>
            </div>

            {isSuperAdmin && Array.isArray(groq.models) && (
              <div className="sound-setting-option vertical">
                <div className="sound-setting-info">
                  <span className="sound-setting-label">🧠 {t('groqModel') || 'Modèle Whisper'}</span>
                  <span className="sound-setting-desc">{t('groqModelDesc') || 'turbo = plus rapide, v3 = plus précis'}</span>
                </div>
                <select
                  value={groq.sttModel || 'whisper-large-v3-turbo'}
                  onChange={e => updateGroq({ sttModel: e.target.value })}
                  className="setup-input"
                  disabled={groqSaving}
                  style={{ maxWidth: '280px' }}
                >
                  {groq.models.map(m => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </select>
              </div>
            )}

            <div className="sound-setting-option vertical" style={{ marginTop: '8px' }}>
              <div className="sound-setting-info">
                <span className="sound-setting-label">🎙️ {t('groqSttTest') || 'Tester la reconnaissance vocale'}</span>
                <span className="sound-setting-desc">{t('mistralSttTestDesc') || 'Enregistrez votre voix pour la transcrire en texte'}</span>
              </div>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                {!groqRecording ? (
                  <button
                    className="btn-primary btn-small"
                    onClick={startGroqRecording}
                    disabled={groqSttBusy || !groq.sttEnabled || (groqAvail && !groqAvail.available)}
                  >
                    ⏺ {t('mistralRecord') || 'Enregistrer'}
                  </button>
                ) : (
                  <button className="btn-danger btn-small" onClick={stopGroqRecording}>
                    ⏹ {t('mistralStop') || 'Arrêter'}
                  </button>
                )}
                {groqSttBusy && <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{t('transcribing') || 'Transcription...'}</span>}
                {groqRecording && <span style={{ fontSize: '12px', color: '#ef4444' }}>● {t('recording') || 'Enregistrement...'}</span>}
              </div>
              {groqSttText && (
                <div style={{ marginTop: '8px', padding: '8px 10px', background: 'var(--bg-hover)', borderRadius: '8px', fontSize: '13px' }}>
                  {groqSttText}
                </div>
              )}
            </div>

            {groqMsg && (
              <p style={{ fontSize: '12px', color: groqMsg.includes('Échec') || groqMsg.includes('refusé') ? '#ef4444' : '#10b981', marginTop: '8px' }}>
                {groqMsg}
              </p>
            )}
          </>
        )}
      </div>
    )}


      <div className="sound-settings-footer">
        <button className="btn-ghost btn-small" onClick={resetDefaults}>
          ↺ {t('resetDefaults') || 'Reset Defaults'}
        </button>
        {message && <span className="sound-settings-message">{message}</span>}
      </div>
    </div>
  );
}

